import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ScaleIcon, 
  ChartBarIcon, 
  ArrowPathIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  CheckCircleIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';
// Removed mock data import - using real data from API
import { calculatePMP, calculateFIFO, calculateLIFO, calculateCUMP, calculateWilson } from '../../utils/valuationMethods';
import testValuationMethods from '../../utils/testValuation';

/**
 * Helper function to get product movements from stockItems
 * 
 * @param {Object} product - The product object
 * @param {Array} stockItems - All stock movements
 * @returns {Array} Filtered movements for this product
 */
const getProductMovements = (product, stockItems) => {
  // Validate inputs
  if (!product || !product.id) {
    console.warn('Invalid product provided to getProductMovements');
    return [];
  }
  
  if (!stockItems || !Array.isArray(stockItems)) {
    console.warn('Invalid stockItems provided to getProductMovements');
    return [];
  }
  
  try {
    // Filter movements for this product
    const movements = stockItems.filter(item => {
      if (!item) return false;
      
      // Direct match on productId
      if (item.productId === product.id) return true;
      
      // Check in items array if present
      if (item.items && Array.isArray(item.items)) {
        return item.items.some(i => i && i.id === product.id);
      }
      
      return false;
    });
    
    // Validate movement data
    return movements.map(movement => {
      // Ensure required fields are present
      return {
        ...movement,
        quantity: parseInt(movement.quantity) || 0,
        unitPrice: parseFloat(movement.unitPrice) || parseFloat(product.price) || 0,
        type: movement.type || 'unknown',
        date: movement.date || new Date().toISOString()
      };
    });
  } catch (error) {
    console.error('Error in getProductMovements:', error);
    return [];
  }
};

const StockValuationTab = () => {
  const { products, stockItems: apiStockItems, fetchAllData, stockValuation, refreshInventoryData } = useAppData();
  const [valuationMethod, setValuationMethod] = useState('PMP');
  const [abcClassification, setAbcClassification] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [productCategory, setProductCategory] = useState('all');
  const [totalInventoryValue, setTotalInventoryValue] = useState(0);
  
  // Use a ref to track if we need to force recalculation
  const shouldRecalculate = useRef(false);
  
  // Store calculated results in a stable ref to avoid re-renders
  const calculationResults = useRef({
    PMP: {},
    FIFO: {},
    LIFO: {},
    CUMP: {},
    Wilson: {}
  });
  
  // Use only real API data - no mock data
  const stockItems = useMemo(() => {
    if (!apiStockItems || !Array.isArray(apiStockItems)) {
      console.warn('No API stock items available');
      return [];
    }
    
    // Process stock items to ensure they have unit prices
    return apiStockItems.map(item => {
      // If the item doesn't have a unitPrice, try to find it from product data
      if (!item.unitPrice && item.productId) {
        const product = products.find(p => p.id === item.productId);
        if (product) {
          return {
            ...item,
            unitPrice: parseFloat(product.price) || 0
          };
        }
      }
      return item;
    });
  }, [apiStockItems, products]);

  // Options de méthodes de valorisation
  const valuationMethods = [
    { id: 'PMP', name: 'Prix Moyen Pondéré (PMP)', description: 'Prix moyen de tous les stocks acquis' },
    { id: 'FIFO', name: 'First In First Out (FIFO)', description: 'Premier Entré, Premier Sorti' },
    { id: 'LIFO', name: 'Last In First Out (LIFO)', description: 'Dernier Entré, Premier Sorti' },
    { id: 'CUMP', name: 'Coût Unitaire Moyen Pondéré', description: 'Coût total des achats / Quantité totale' },
    { id: 'Wilson', name: 'Modèle de Wilson', description: 'Quantité économique optimale' }
  ];

  /**
   * Calculate valuation for a single product using the specified method
   * 
   * @param {Object} product - The product to calculate valuation for
   * @param {string} method - The valuation method to use
   * @returns {Object} Valuation result with unitPrice, totalValue, and totalQuantity
   */
  const calculateProductValuation = useCallback((product, method) => {
    if (!product || !product.id) {
      console.error('Invalid product provided to calculateProductValuation');
      return { unitPrice: 0, totalValue: 0, totalQuantity: 0 };
    }
    
    if (!method || typeof method !== 'string') {
      console.error('Invalid method provided to calculateProductValuation');
      method = 'PMP'; // Default to PMP
    }
    
    try {
      // Get movements for this product
      const productMovements = getProductMovements(product, stockItems);
      
      // Log for debugging
      console.log(`Calculating ${method} for ${product.name} with ${productMovements.length} movements`);
      
      // Apply the selected valuation method
      let result;
      switch (method) {
        case 'PMP':
          result = calculatePMP(product, productMovements);
          break;
        case 'FIFO':
          result = calculateFIFO(product, productMovements);
          break;
        case 'LIFO':
          result = calculateLIFO(product, productMovements);
          break;
        case 'CUMP':
          result = calculateCUMP(product, productMovements);
          break;
        case 'Wilson':
          result = calculateWilson(product, productMovements);
          break;
        default:
          result = calculatePMP(product, productMovements);
      }
      
      // Validate result
      if (!result || typeof result !== 'object') {
        console.error(`Invalid result from ${method} calculation for ${product.name}`);
        return { unitPrice: 0, totalValue: 0, totalQuantity: 0 };
      }
      
      // Ensure all required fields are present and valid
      return {
        unitPrice: parseFloat(result.unitPrice) || 0,
        totalValue: parseFloat(result.totalValue) || 0,
        totalQuantity: parseInt(result.totalQuantity) || 0
      };
    } catch (error) {
      console.error(`Error calculating ${method} for ${product.name}:`, error);
      return { unitPrice: 0, totalValue: 0, totalQuantity: 0 };
    }
  }, [stockItems]);
  
  /**
   * Memoized calculation function that uses the cache
   * Calculates valuation for all products using the current method
   */
  const calculateValuation = useCallback(async () => {
    setIsCalculating(true);
    
    try {
      // Refresh inventory data to get latest valuation from API
      try {
        if (refreshInventoryData) await refreshInventoryData();
        if (fetchAllData) await fetchAllData();
      } catch (refreshError) {
        console.error('Error refreshing data:', refreshError);
        // Continue with existing data
      }
      
      if (!products || !Array.isArray(products) || products.length === 0) {
        console.warn('No products available for valuation calculation');
        setTotalInventoryValue(0);
        setAbcClassification([]);
        setIsCalculating(false);
        return;
      }
      
      // Log for debugging
      console.log(`Calculating valuation for ${products.length} products using ${valuationMethod} method`);
      
      if (products && products.length > 0) {
        // Clear cache if we're forcing a recalculation
        if (shouldRecalculate.current) {
          calculationResults.current = {
            PMP: {},
            FIFO: {},
            LIFO: {},
            CUMP: {},
            Wilson: {}
          };
          shouldRecalculate.current = false;
        }
        
        // Calculate for all products using the current method
        const valuatedProducts = products.map(product => {
          // Check if we have a cached result and use it if available
          if (!shouldRecalculate.current && 
              calculationResults.current[valuationMethod] && 
              calculationResults.current[valuationMethod][product.id]) {
            return calculationResults.current[valuationMethod][product.id];
          }
          
          // Calculate new result
          const result = calculateProductValuation(product, valuationMethod);
          
          // Create product with valuation data
          const productWithValuation = {
          ...product,
            valuatedUnitPrice: result.unitPrice,
            totalValue: result.totalValue,
            classification: '' // Will be set later
          };
          
          // Ensure the method's cache object exists
          if (!calculationResults.current[valuationMethod]) {
            calculationResults.current[valuationMethod] = {};
          }
          
          // Cache the result
          calculationResults.current[valuationMethod][product.id] = productWithValuation;
          
          return productWithValuation;
        });
        
        // Calculate total inventory value
        const totalValue = valuatedProducts.reduce((sum, product) => {
          // Ensure we're adding a number
          const value = parseFloat(product.totalValue) || 0;
          return sum + value;
        }, 0);
        
        // Sort products by value in descending order
        const sortedProducts = [...valuatedProducts].sort((a, b) => {
          const valueA = parseFloat(b.totalValue) || 0;
          const valueB = parseFloat(a.totalValue) || 0;
          return valueA - valueB;
        });
        
        // Apply ABC classification
        let cumulativeValue = 0;
        const classifiedProducts = sortedProducts.map(product => {
          // Ensure value is a number
          const value = parseFloat(product.totalValue) || 0;
          cumulativeValue += value;
          const percentOfTotal = totalValue > 0 ? (cumulativeValue / totalValue) * 100 : 0;
          
          // Classification ABC basée sur la courbe de Pareto
        let classification;
        if (percentOfTotal <= 80) {
          classification = 'A'; // Produits à haute valeur (80% de la valeur totale)
        } else if (percentOfTotal <= 95) {
          classification = 'B'; // Produits à valeur moyenne (15% de la valeur)
        } else {
          classification = 'C'; // Produits à faible valeur (5% de la valeur)
        }
        
          const updatedProduct = {
          ...product,
            classification,
            percentOfTotal: parseFloat(percentOfTotal.toFixed(2))
        };
          
          // Update the cache with the classification
          calculationResults.current[valuationMethod][product.id] = updatedProduct;
          
          return updatedProduct;
      });
      
        console.log(`Total inventory value (${valuationMethod}): ${totalValue}`);
        setTotalInventoryValue(totalValue);
      setAbcClassification(classifiedProducts);
        // Success message without toast to avoid UI clutter
        console.log(`Valorisation calculée selon la méthode ${valuationMethod}`);
      } else {
        console.warn('Aucun produit disponible pour calculer la valorisation');
      }
    } catch (error) {
      console.error('Error calculating valuation:', error);
              // Handle error silently without showing toast
        console.error('Error calculating valuation:', error);
    } finally {
      setIsCalculating(false);
    }
  }, [products, valuationMethod, refreshInventoryData, fetchAllData, calculateProductValuation]);
  
  // Handle method change without triggering recalculation
  const handleMethodChange = useCallback((methodId) => {
    setValuationMethod(methodId);
    
    try {
      // Check if we have cached results for this method
      const hasCache = products && products.length > 0 && 
                       calculationResults.current[methodId] && 
                       Object.keys(calculationResults.current[methodId]).length > 0;
      
      if (hasCache) {
        console.log(`Using cached results for ${methodId}`);  
        
        // Get cached results for this method
        const cachedProducts = products.map(product => {
          const cachedProduct = calculationResults.current[methodId][product.id];
          return cachedProduct || product;
        });
        
        // Calculate total value from cached results
        const totalValue = cachedProducts.reduce((sum, product) => {
          // Ensure we're adding a number
          const value = parseFloat(product.totalValue) || 0;
          return sum + value;
        }, 0);
        
        console.log(`Total inventory value (${methodId}): ${totalValue}`);
        setTotalInventoryValue(totalValue);
        setAbcClassification(cachedProducts);
      } else {
        console.log(`No cache for ${methodId}, calculating...`);
        // Calculate if no cache exists
        calculateValuation();
      }
    } catch (error) {
      console.error('Error changing valuation method:', error);
    }
  }, [products, calculateValuation]);
  
  // Initial calculation on component mount
  useEffect(() => {
    // Wrap in try-catch to prevent errors from breaking the component
    try {
      // Calculate all methods upfront to populate the cache
      const calculateAllMethods = async () => {
        setIsCalculating(true);
        
        try {
          // Clear the cache
          calculationResults.current = {
            PMP: {},
            FIFO: {},
            LIFO: {},
            CUMP: {},
            Wilson: {}
          };
          
          // Calculate for each method
          for (const method of ['PMP', 'FIFO', 'LIFO', 'CUMP', 'Wilson']) {
            if (!products || products.length === 0) continue;
            
            // Calculate for all products using this method
            const valuatedProducts = products.map(product => {
              // Calculate result
              const result = calculateProductValuation(product, method);
              
              // Create product with valuation data
              const productWithValuation = {
                ...product,
                valuatedUnitPrice: result.unitPrice,
                totalValue: result.totalValue,
                classification: ''
              };
              
              // Ensure the method's cache object exists
              if (!calculationResults.current[method]) {
                calculationResults.current[method] = {};
              }
              
              // Cache the result
              calculationResults.current[method][product.id] = productWithValuation;
              
              return productWithValuation;
            });
            
            // If this is the current method, update the state
            if (method === valuationMethod) {
              // Apply ABC classification
              const totalValue = valuatedProducts.reduce((sum, product) => {
                // Ensure we're adding a number
                const value = parseFloat(product.totalValue) || 0;
                return sum + value;
              }, 0);
              
              // Sort products by value (highest to lowest)
              const sortedProducts = [...valuatedProducts].sort((a, b) => {
                const valueA = parseFloat(b.totalValue) || 0;
                const valueB = parseFloat(a.totalValue) || 0;
                return valueA - valueB;
              });
              
              let cumulativeValue = 0;
              const classifiedProducts = sortedProducts.map(product => {
                // Ensure value is a number
                const value = parseFloat(product.totalValue) || 0;
                cumulativeValue += value;
                const percentOfTotal = totalValue > 0 ? (cumulativeValue / totalValue) * 100 : 0;
                
                let classification;
                if (percentOfTotal <= 80) {
                  classification = 'A';
                } else if (percentOfTotal <= 95) {
                  classification = 'B';
                } else {
                  classification = 'C';
                }
                
                const updatedProduct = {
                  ...product,
                  classification,
                  percentOfTotal: parseFloat(percentOfTotal.toFixed(2))
                };
                
                // Update the cache
                calculationResults.current[method][product.id] = updatedProduct;
                
                return updatedProduct;
              });
              
              console.log(`Total inventory value (${method}): ${totalValue}`);
              setTotalInventoryValue(totalValue);
              setAbcClassification(classifiedProducts);
            }
          }
          
          console.log('All valuation methods calculated and cached');
        } catch (error) {
          console.error('Error calculating all methods:', error);
        } finally {
          setIsCalculating(false);
        }
      };
      
      // Run the calculation
      calculateAllMethods();
      
      // Set up a periodic refresh every 5 minutes
      const refreshInterval = setInterval(() => {
        shouldRecalculate.current = true;
        calculateAllMethods();
      }, 300000);
      
      return () => clearInterval(refreshInterval);
    } catch (error) {
      console.error('Error in initial calculation:', error);
      setIsCalculating(false);
    }
  }, [calculateProductValuation, products, valuationMethod]);
  
  // Filter products by category
  const filteredProducts = useMemo(() => {
    return productCategory === 'all' 
    ? abcClassification 
    : abcClassification.filter(p => p.classification === productCategory);
  }, [abcClassification, productCategory]);

  // Stats for ABC classification
  const statsABC = useMemo(() => [
    {
      name: 'Classe A',
      value: abcClassification.filter(p => p.classification === 'A').length,
      percentage: Math.round((abcClassification.filter(p => p.classification === 'A').length / abcClassification.length) * 100) || 0,
      color: 'from-green-500 to-green-600',
      description: 'Produits à haute valeur'
    },
    {
      name: 'Classe B',
      value: abcClassification.filter(p => p.classification === 'B').length,
      percentage: Math.round((abcClassification.filter(p => p.classification === 'B').length / abcClassification.length) * 100) || 0,
      color: 'from-blue-500 to-blue-600',
      description: 'Produits à valeur moyenne'
    },
    {
      name: 'Classe C',
      value: abcClassification.filter(p => p.classification === 'C').length,
      percentage: Math.round((abcClassification.filter(p => p.classification === 'C').length / abcClassification.length) * 100) || 0,
      color: 'from-gray-500 to-gray-600',
      description: 'Produits à faible valeur'
    }
  ], [abcClassification]);

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {/* Titre et explication */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center">
              <ScaleIcon className="h-6 w-6 mr-2" />
              Valorisation du stock
            </h3>
            <p className="text-white/60 mt-1">
              Évaluez la valeur de votre stock et appliquez la classification ABC
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => {
                try {
                  // Force recalculation of all methods
                  shouldRecalculate.current = true;
                  setIsCalculating(true);
                  
                  // Clear the cache
                  calculationResults.current = {
                    PMP: {},
                    FIFO: {},
                    LIFO: {},
                    CUMP: {},
                    Wilson: {}
                  };
                  
                  // Recalculate the current method
                  calculateValuation().catch(err => {
                    console.error('Recalculation error:', err);
                    setIsCalculating(false);
                  });
                } catch (error) {
                  console.error('Error triggering recalculation:', error);
                  setIsCalculating(false);
                }
              }}
              disabled={isCalculating}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              <ArrowPathIcon className="h-5 w-5" />
              <span>{isCalculating ? 'Calcul en cours...' : 'Recalculer'}</span>
            </button>
            <button 
              onClick={() => {
                try {
                  // Run test function
                  testValuationMethods();
                  toast.success('Tests exécutés. Voir la console pour les résultats.');
                } catch (error) {
                  console.error('Error running tests:', error);
                  toast.error('Erreur lors de l\'exécution des tests');
                }
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors"
            >
              <BeakerIcon className="h-5 w-5" />
              <span>Tester</span>
            </button>
            <button className="flex items-center space-x-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-colors">
              <DocumentArrowDownIcon className="h-5 w-5" />
              <span>Exporter</span>
            </button>
          </div>
        </div>
        
        {/* Total Inventory Value Display */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex justify-between items-center">
            <div>
              <span className="text-white/70">Valeur totale du stock selon la méthode </span>
              <span className="font-semibold text-white">{valuationMethod}</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {totalInventoryValue.toLocaleString()} DA
            </div>
          </div>
        </div>
      </div>

      {/* Méthodes de valorisation */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h4 className="text-lg font-medium text-white mb-4">Méthode de valorisation</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {valuationMethods.map((method) => (
            <div
              key={method.id}
              onClick={() => handleMethodChange(method.id)}
              className={`cursor-pointer p-4 rounded-xl transition-colors border ${
                valuationMethod === method.id 
                  ? 'bg-blue-600/20 border-blue-500/50' 
                  : 'bg-white/5 border-white/10 hover:border-white/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-white">{method.id}</h5>
                {valuationMethod === method.id && (
                  <CheckCircleIcon className="h-5 w-5 text-blue-400" />
                )}
              </div>
              <p className="text-sm text-white/80 mt-1">{method.name}</p>
              <p className="text-xs text-white/60 mt-2">{method.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Classification ABC - Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statsABC.map((stat) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-white">{stat.name}</p>
                <p className="text-white/60 text-sm">{stat.description}</p>
              </div>
              <div className={`p-3 rounded-2xl bg-gradient-to-br ${stat.color}`}>
                <ChartBarIcon className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between items-center">
                <span className="text-2xl font-bold text-white">{stat.value} produits</span>
                <span className="text-white/80 text-sm">{stat.percentage}% du total</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-2 mt-2">
                <div 
                  className={`h-2 rounded-full bg-gradient-to-r ${stat.color}`} 
                  style={{ width: `${stat.percentage}%` }}
                ></div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Liste des produits avec classification */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
        <div className="p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-white/10">
          <h4 className="text-lg font-medium text-white">Classification ABC des produits</h4>
          
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-white/50" />
              <select
                value={productCategory}
                onChange={(e) => setProductCategory(e.target.value)}
                className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">Toutes les classes</option>
                <option value="A">Classe A uniquement</option>
                <option value="B">Classe B uniquement</option>
                <option value="C">Classe C uniquement</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-white/10">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-white/70 uppercase tracking-wider">Produit</th>
                <th className="px-6 py-4 text-center text-xs font-medium text-white/70 uppercase tracking-wider">Classe</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Stock</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Prix unitaire</th>
                <th className="px-6 py-4 text-right text-xs font-medium text-white/70 uppercase tracking-wider">Valeur totale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {filteredProducts.map((product, index) => (
                <motion.tr 
                  key={product.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-white">{product.name}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full ${
                      product.classification === 'A' ? 'bg-green-500/20 text-green-400' :
                      product.classification === 'B' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-gray-500/20 text-gray-400'
                    } font-semibold`}>
                      {product.classification}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                    {product.stock} {product.unit || 'unités'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-white">
                    {(product.valuatedUnitPrice || product.price).toLocaleString()} DA
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-white">
                    {product.totalValue.toLocaleString()} DA
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </motion.div>
  );
};

export default StockValuationTab; 